/* eslint-disable no-plusplus */
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import postscribe from "postscribe";
import {withRouter} from "react-router-dom";
import {gql} from 'apollo-boost';
import {Query} from 'react-apollo';
import {Helmet} from "react-helmet";
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import Paragraph from "../components/Paragraph";
import { yitmScroll } from "../helpers/yitmScroll";
import PageNotFound from "./404/404";
import './ComponentPage.scss';

interface Data {
    nodeById: {
        entityLabel: string;
        entityId: string;
        entityBundle: string;
        entityMetatags: Array<{
            key: string;
            value: string;
        }>;
        fieldComponents: Array<{targetRevisionId: string}>;
        fieldSecondaryNavigationMenu: Array<{targetId: number}>;
        fieldShowMenuAfterHero: boolean;
    }
}

class ComponentPageQuery extends Query<Data, {id: string}> {}

const GET_PAGE_INNER = `
  entityLabel
  entityId
  entityBundle
  entityMetatags {
    key
    value
  }
  ... on NodeLandingPage {
    entityLabel
    fieldComponents {
        targetRevisionId
    }
    fieldSecondaryNavigationMenu {
      targetId
    }
    fieldShowMenuAfterHero
  }
  ... on NodeLegalPage {
    entityLabel
    fieldComponents {
        targetRevisionId
    }
    fieldSecondaryNavigationMenu {
      targetId
    }
    fieldShowMenuAfterHero
  }
  ... on NodeDealerRankingsPage {
    entityLabel
    fieldComponents {
        targetRevisionId
    }
    fieldSecondaryNavigationMenu {
      targetId
    }
    fieldShowMenuAfterHero
  }
  ... on NodePostTradePage {
    entityLabel
    fieldComponents:fieldPostTradeComponent {
        targetRevisionId
    }
  }
  ... on NodeMobileOptimizedPage {
    entityLabel
    fieldComponents: fieldMobileSections {
        targetRevisionId
    }
  }
`;

const GET_PAGE = gql`
query nodeById($id: String!) {
  nodeById(id: $id) {
    ${GET_PAGE_INNER}
  }
}
`;

const GET_REVISION = gql`
    query nodeRevisionById($id: String!) {
        nodeById:nodeRevisionById(id: $id) {
            ${GET_PAGE_INNER}
        }
    }
`;


/**
 * Loads a Bricks/Paragraphs based page with components
 *
 * @param id
 * @param statusCode
 * @param revisionId
 * @param location
 * @constructor
 */
class ComponentPage extends Component {
    static propTypes = {
        id: PropTypes.string.isRequired,
        statusCode: PropTypes.string,
        revisionId: PropTypes.string,
        // eslint-disable-next-line react/forbid-prop-types
        location: PropTypes.object.isRequired,
        // eslint-disable-next-line react/forbid-prop-types
        pageNotFoundEntity: PropTypes.object
    };

    static defaultProps = {
        statusCode: '',
        revisionId: '',
        pageNotFoundEntity: null
    };

    state = {
        allComponentsLoaded: false,
        scriptsLoaded: false,
        scriptsLoadedTimer: null,
        cssID: null
    };

    // eslint-disable-next-line react/sort-comp
    scriptsLoadedTimer = () => {
        this.setState({scriptsLoadedTimer: setInterval(() => {
            if (!this.state.allComponentsLoaded) return;

            if (this.state.scriptsLoaded) {
                clearInterval(this.state.scriptsLoadedTimer);
                return;
            }

            if (this.loadLegalPageScripts(this.state.cssID)) {
                this.setState({scriptsLoaded: true});
            }
        }, 500)});
    }

    componentWillMount() {
        this.scriptsLoadedTimer();
    }

    componentDidMount() {
        let index = 1;
        const loop = setInterval(() => {
            index++;
            if(index > 10) {
                index = 0;
                clearInterval(loop)
            }
            yitmScroll();
        }, 500)
    }

    componentWillUnmount() {
        clearInterval(this.state.scriptsLoadedTimer);
    }

    getLoaderClass = () => {
        if ( !this.props.location.hash ) return 'fullPageLoader--hidden';

        const loaderOverlay = document.querySelector('.fullPageLoader--generic');
        const loadedState = this.state.allComponentsLoaded;

        if (loadedState) {
            clearAllBodyScrollLocks();
            return 'fullPageLoader fullPageLoader--hidden'
        }


        disableBodyScroll(loaderOverlay);
        return 'fullPageLoader';
    };

    handleLoadedComponents = (totalComponents, loadedComponents) => {

        // Only allow state to set once
        if (this.state.allComponentsLoaded) return;

        const loadedComponentsValues = Object.values(loadedComponents);

        // Uncomment to debug nested component loading state
        /*
        console.log(loadedComponents);
        console.log(loadedComponentsValues.length);
        console.log(totalComponents);
        console.log("Remaining");
        console.log(loadedComponentsValues.filter(val => !val).length);
        */

        // All Components have Loaded
        if (loadedComponentsValues && loadedComponentsValues.length && loadedComponentsValues.length >= totalComponents && loadedComponentsValues.filter(val => !val).length === 0) {
            this.setState({allComponentsLoaded: true});

            // form assembly requires document load for recaptcha
            try {
                const contentLoadedEvent = document.createEvent("Event");
                contentLoadedEvent.initEvent("DOMContentLoaded", true, true);
                setTimeout(() => {
                    window.document.dispatchEvent(contentLoadedEvent);
                }, 1000);

            } catch(e) {
                console.log("failed: init formassembly");
                console.error(e);
            }

            // Scroll to Section
            if (this.props.location.hash) {
                const element = document.getElementById(this.props.location.hash.replace('#', ''));
                if (element ) {
                    element.scrollIntoView({
                        behavior: "smooth"
                    });
                }
            }
        }
    };

    loadLegalPageScripts = (cssID) => {
        if (!document.getElementById(cssID)) return false;

        // eslint-disable-next-line no-extra-boolean-cast
        if (!!document.querySelector('.upguard-btn')) {
            postscribe(`#${cssID}`, '<script async defer src="https://cdn.cyber-risk.upguard.com/widget.js"></script>');
        }

        return true;
    }

    render() {
       const {id, statusCode, revisionId} = this.props;

       return (
           <ComponentPageQuery asyncMode query={!revisionId ? GET_PAGE : GET_REVISION} variables={{ id: !revisionId ? id : revisionId }}>
               {({ loading, error, data }) => {
                   if (loading) return (<div className="pageLoader"/>);
                   if (error) return <div>{error}</div>;

                   const page = data.nodeById;
                   let title = page.entityMetatags.filter(metaTag => metaTag.key === 'title');
                   if (Object.keys(title).length && title[0]) {
                       title = title[0].value;
                   } else {
                       title = `${page.entityLabel} | MarketAxess`;
                   }

                   // Clone components to allow writing to array
                   const components = page.fieldComponents.slice();

                   // Total Components for Initial array to prevent "Loaded" state on initial load
                   const totalComponents = components.length;

                   // 404 Handler
                   // eslint-disable-next-line eqeqeq
                   if (page.entityId != 157 && (statusCode) && (statusCode === '404')) {
                       if (this.props.pageNotFoundEntity && this.props.pageNotFoundEntity[0] && this.props.pageNotFoundEntity[0].entityId) {
                           return (<ComponentPage {...this.props} pageNotFoundEntity={null} statusCode="404" id={this.props.pageNotFoundEntity[0].entityId}/>);
                       }
                       return (<PageNotFound/>);
                   }

                   // Set CssID in State for Postscribe to target
                   if (!this.state.cssID) this.setState({cssID: `${page.entityBundle}_${page.entityId}`});

                   return (
                       <div id={`${page.entityBundle}_${page.entityId}`} className={`componentPage page-${page.entityId} page-${page.entityBundle}`}>

                           <div className={`fullPageLoader--generic ${this.getLoaderClass()}`}>
                               <span className="fas fa-spinner-third fa-spin"/>
                           </div>

                           <Helmet>
                               {title && (
                                   <title>{title}</title>
                               )}
                               {page.entityMetatags.filter(metaTag => metaTag.key !== 'title' && !metaTag.key.includes('og:')).map(metaTag => (
                                   <meta name={metaTag.key} content={metaTag.value} key={`standardMeta_${metaTag.key}`}/>
                               ))}
                               {page.entityMetatags.filter(metaTag => metaTag.key.includes('og:')).map(metaTag => (
                                   <meta property={metaTag.key} content={metaTag.value} key={`opengraphMeta_${metaTag.key}`}/>
                               ))}
                               {statusCode && (
                                   <meta name="prerender-status-code" content={statusCode}/>
                               )}
                           </Helmet>


                           <div className="main">
                               {components && components.map((component, index) => (
                                   <Paragraph key={`${page.entityId}_${component.targetRevisionId}`}
                                              componentIndex={index}
                                              id={String(component.targetRevisionId)}
                                              handleLoadedComponents={lc => this.handleLoadedComponents(totalComponents, lc)}/>
                               ))}
                           </div>

                       </div>
                   )
               }}
           </ComponentPageQuery>
       );

   }
}

export default withRouter(ComponentPage);



// WEBPACK FOOTER //
// ./frontend/pages/ComponentPage.jsx